import { defineStore } from "pinia";
import router from "@/router";
import { setAuthCookies } from "@/api/auth-cookies";
import { decodeJwtPayload } from "@/helper/jwt";
import { createIconRequestQueue } from "@/helper/iconRequestQueue";
import { clearMaster } from "@/helper/passwordKeyStore";
import {
  ref,
  computed,
} from "vue";

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

import type {
  ApiBarcode,
  ApiObject,
  ApiObjectWithReferences,
  IconName,
  IconSet,
  IconStyle,
  PhotoToInsert,
  SVG,
} from "@/models/api";
import type { AccountStateResponse, ProviderAccounts } from "@/models/accounts";
import type {
  CustomField,
  CustomFieldCreate,
  CustomFieldPatch,
  CustomFieldValue,
} from "@/models/custom-fields";
import type {
  CustomSchemaCreate,
  CustomSchemaOut,
  CustomSchemaPatch,
  ObjectSchemaOut,
  CustomSchemaEntryCreate,
  CustomSchemaEntryPatch,
  CustomSchemaValueOut,
  SchemaImportResult,
  SchemaVisibility,
} from "@/models/custom-schemas";
import type {
  CustomFieldId,
  CustomFieldValueId,
  CustomSchemaId,
  CustomSchemaEntryId,
  CustomSchemaValueId,
} from "@/models/branded-ids";
import {
  getApiUrl,
  OBJECT_TYPE_CONTAINER,
  OBJECT_TYPE_ITEM,
} from "@/data/constants";
import { iconObjectToIconString } from "@/helper/icons";
import { deleteUser } from "@/api/services/object.service";
import { http } from "@/api/http";
import { createClient } from "@/api/client";
import type { MoveObject } from "@/models/generated-api-schema/models/MoveObject";
import type { ObjectCreate } from "@/models/generated-api-schema/models/ObjectCreate";
import type { ObjectPatch } from "@/models/generated-api-schema/models/ObjectPatch";
import type { BarcodeCreate } from "@/models/generated-api-schema/models/BarcodeCreate";
import type {
  IconMapping,
  IconWithName,
  ObjectsMappedByID,
} from "@/types/apiStore";
import { isApiObject } from "@/types/apiStore";
import { getObjectId } from "@/helper/getObjectId";
import { nullToUndefined } from "@/helper/undefinedToNull";
import { compareObjectOrder } from "@/helper/objectOrder";


/** How many icons one `/icons/svg/batch` request asks for (the server accepts up to 32). */
const ICON_BATCH_SIZE = 8;

export function sortById(items: ApiObjectWithReferences[]): ObjectsMappedByID {
  // heavily based on https://stackoverflow.com/a/15713912/3423324#create-javascript-tree-out-of-list-of-objects
  const objectsByID: ObjectsMappedByID = [];

  function parentId(item: ApiObject): null | number {
    // if the item's contained_in_object is empty, return null
    // noinspection PointlessBooleanExpressionJS
    if (
      false
      || item === undefined
      || item.contained_in_object === undefined
      || item.contained_in_object === null
    ) {
      return null;
    }
    // check if item.contained_in_object is an object itself, then use its id.
    if (typeof item.contained_in_object === "object" || !isApiObject({})) {
      // version for browsers.
      if (isApiObject(item.contained_in_object)) {
        // version for the IDE.
        // the id is empty
        if (
          item.contained_in_object.id === undefined ||
          item.contained_in_object.id === null
        ) {
          return null;
        }
        // the id is there, so it must be a number.
        return item.contained_in_object.id;
      }
    }
    // Now contained_in_object is not an object.
    // we already did check if it's empty
    return item.contained_in_object;
  }

  // you'll want to first put all of your items into a sparse array indexed by their ID and translate everything except the children (which should be present but empty) and including the parent ID:
  items.forEach(function (item) {
    // noinspection PointlessBooleanExpressionJS
    if (
      false
      || item === undefined
      || item.object === undefined
      || item.object.id === undefined
      || item.object.id === null
    ) {
      return;
    }

    // map objects to their id, map undefined to null.
    const contained_in_object = parentId(item.object);

    // also add it to the by-id list
    objectsByID[item.object.id] = {
      data: item,
      children: [],
      parentID: contained_in_object,
    };
  });

  // Then you'll want to go through all the items, adding children to their parents:
  items.forEach(function (item) {
    const parentID = parentId(item.object);
    if (parentID !== null) {
      objectsByID[parentID].children.push(item);
    }
  });
  objectsByID.forEach((item) => {
    item.children.sort(compareObjectOrder);
  });
  return objectsByID;
}

function nextSortOrderForParent(
  items: ApiObjectWithReferences[],
  parentId: number | null,
  excludeId: number | null = null
): number {
  const siblingOrders = items
    .filter((item) => {
      const containedInObject = item.object.contained_in_object ?? null;
      return containedInObject === parentId && item.object.id !== excludeId;
    })
    .map((item) => item.object.sort_order ?? 0);
  return Math.max(0, ...siblingOrders) + 1;
}

export const useApiStore = defineStore(
  "api",
  () => {
  // STATE
    const objects = ref<ApiObjectWithReferences[]>([]);
    const schemas = ref<CustomSchemaOut[]>([]);
    const objectsByID = computed<ObjectsMappedByID>(() => sortById(objects.value));
    const objectsAtRoot = computed(() => objectsByID.value.filter(function (item) {
      return item.parentID === null;
    }).sort((left, right) => compareObjectOrder(left.data, right.data)));
    const fetchedIcons = ref<IconMapping>({});
    // Shared registry of icon requests: de-duplicates, limits concurrency, remembers failures.
    const iconRequests = createIconRequestQueue();
    const iconNames = ref<string[]>([]); // set by fetchAllIconNames
    const accessToken = ref<string | null>(window.localStorage.getItem('accessToken'));
    const imagesToken = ref<string | null>(window.localStorage.getItem('imagesToken'));

    const items = computed(() => objects.value.filter(
      (obj) => obj.object.type === OBJECT_TYPE_ITEM
    ));
    const containers = computed<ApiObjectWithReferences[]>(() => objects.value.filter(
      (obj) => obj.object.type === OBJECT_TYPE_CONTAINER
    ));

    const allIconNames = computed<string[]>(() => {
      // When comparing large numbers of strings, such as in sorting large arrays, it is better
      // to create an `Intl.Collator` object and use the function provided by its `compare` property.
      const sorter = new Intl.Collator(undefined, {
        usage: "sort",
        numeric: true,
        sensitivity: "base",
      });
      return iconNames.value.sort(sorter.compare);
    });
    const allIconSvgMetas = computed<IconWithName[]>(() => {
      // sort them by key:
      // When comparing large numbers of strings, such as in sorting large arrays, it is better
      // to create an `Intl.Collator` object and use the function provided by its `compare` property.
      const sorter = new Intl.Collator(undefined, {
        usage: "sort",
        numeric: true,
        sensitivity: "base",
      });
      return Object.entries(fetchedIcons.value)
        .sort(([k1, _1], [k2, _2]) => sorter.compare(k1, k2))
        .map(([k, v]) => {
          return { name: k, meta: v };
        });
    });

    const byId = (id: number) => objectsByID.value[id] ?? null;

    function iconSvgMeta(
      type: IconSet,
      name: IconName,
      style: IconStyle
    ): SVG | null {
      return fetchedIcons.value[`${type}.${name}.${style}`] ?? null;
    }

    // setter
    function setAccessToken(token: string | null) {
      accessToken.value = token;
    }
    function setImagesToken(token: string | null) {
      imagesToken.value = token;
    }

    function clearTokenRefresh() {
      if (_refreshTimer !== null) {
        clearTimeout(_refreshTimer);
        _refreshTimer = null;
      }
    }

    async function refreshToken() {
      try {
        const response = await http.post<{ access: { access_token: string }; images: { access_token: string } }>(
          "/api/v1/auth/refresh",
        );
        const newAccess = response.data.access.access_token;
        const newImages = response.data.images.access_token;
        accessToken.value = newAccess;
        imagesToken.value = newImages;
        localStorage.setItem("accessToken", newAccess);
        localStorage.setItem("imagesToken", newImages);
        setAuthCookies(newAccess, newImages, true);
        scheduleTokenRefresh();
      } catch {
        clearTokenRefresh();
        await clearMaster();
        router.push({ name: "Login" });
      }
    }

    function scheduleTokenRefresh() {
      const token = accessToken.value;
      if (!token) return;
      const payload = decodeJwtPayload(token);
      if (!payload?.remember_me) return;
      const expMs = (payload.exp ?? 0) * 1000;
      const remaining = expMs - Date.now();
      if (remaining <= 0) {
        router.push({ name: "Login" });
        return;
      }
      clearTokenRefresh();
      _refreshTimer = setTimeout(() => { void refreshToken(); }, remaining * 0.8);
    }

    // actions
    function fetchObjects() {
      console.log("fetching all items.");
      const baseURL = `${getApiUrl()}/items/all`;
      http
        .get(baseURL)
        .then((response) => {
          // noinspection TypeScriptValidateJSTypes
          objects.value = response.data;
        })
        .catch((e) => {
          console.error(e);
        });
    }
    function fetchIcon(payload: {
      type: IconSet;
      name: IconName;
      style: IconStyle;
    }): Promise<void> {
      const iconName = iconObjectToIconString(payload);
      if (fetchedIcons.value[iconName] !== undefined) {
        return Promise.resolve();
      }

      const baseURL = `${getApiUrl()}/icons/svg/${iconName}/meta`;
      return iconRequests.request(iconName, () =>
        http
          .get(baseURL)
          .then((response) => {
            // noinspection TypeScriptValidateJSTypes
            fetchedIcons.value[iconName] = response.data as SVG;
          })
          .catch((e) => {
            console.error(e);
            throw e; // lets the queue remember the failure
          }),
      );
    }
    /**
     * Fetch several icons with as few requests as possible (chunks of `ICON_BATCH_SIZE`).
     * Icons which are loaded, pending or failed recently are left out, and the icons of a chunk are registered as pending
     * right away, so `fetchIcon` for one of them waits for the batch instead of requesting it again.
     */
    function fetchIcons(
      payloads: Array<{ type: IconSet; name: IconName; style: IconStyle }>
    ): Promise<void> {
      const wanted = [
        ...new Set(payloads.map((payload) => iconObjectToIconString(payload))),
      ].filter(
        (iconName) =>
          fetchedIcons.value[iconName] === undefined &&
          !iconRequests.isPending(iconName) &&
          !iconRequests.isRecentlyFailed(iconName)
      );
      const baseURL = `${getApiUrl()}/icons/svg/batch`;
      const chunks: Promise<void>[] = [];
      for (let i = 0; i < wanted.length; i += ICON_BATCH_SIZE) {
        const names = wanted.slice(i, i + ICON_BATCH_SIZE);
        const request = http
          .get(baseURL, { params: { names }, paramsSerializer: { indexes: null } })
          .then((response) => {
            const icons = response.data as IconMapping;
            Object.entries(icons).forEach(
              ([name, svg]) => (fetchedIcons.value[name] = svg)
            );
            return icons;
          })
          .catch((e) => {
            console.error(e);
            throw e;
          });
        // One registration per icon: a name the server left out fails on its own,
        // the others of the chunk stay loaded.
        names.forEach((name) =>
          chunks.push(
            iconRequests.register(
              [name],
              request.then((icons) => {
                if (icons[name] === undefined) {
                  throw new Error(`Icon not found: ${name}`);
                }
              })
            )
          )
        );
      }
      return Promise.all(chunks).then(() => undefined);
    }
    function isIconPending(iconName: string): boolean {
      return iconRequests.isPending(iconName);
    }
    function fetchAllIcons() {
      // const { dispatch, commit, getters, state } = apiActionContext(context)
      const baseURL = `${getApiUrl()}/icons/svg/all/meta`;
      http
        .get(baseURL)
        .then((response) => {
          const icons: IconMapping = response.data;
          // noinspection TypeScriptValidateJSTypes
          Object.entries(icons).forEach(
            ([name, svg]) => (fetchedIcons.value[name] = svg)
          );
        })
        .catch((e) => {
          console.error(e);
        });
    }
    function fetchAllIconNames() {
      const baseURL = `${getApiUrl()}/icons/names/all`;
      http
        .get(baseURL)
        .then((response) => {
          // noinspection TypeScriptValidateJSTypes
          iconNames.value = response.data as string[];
        })
        .catch((e) => {
          console.error(e);
        });
    }
    async function deleteItem(payload: { id: number }) {
      // const { dispatch, commit, getters, state } = apiActionContext(context)
      await deleteUser(payload.id); // TODO: it says "User" but it's actually an "Item".
      // keep only items where the id is not the one to delete
      objects.value = objects.value.filter(
        (item) => item.object.id !== payload.id
      );
    }
    async function moveObject(move: MoveObject) {
      const moveObjectWithId = getObjectId(move.id);
      if ((moveObjectWithId ?? null) === null) {
        console.error("moveObject called with invalid id:", move);
        return;
      }
      let moveToId = getObjectId(move.move_to);
      if (moveToId === null || moveToId < 0) {
        // helper for when you only have a number input which can't do null.
        moveToId = null;
      }

      const appClient = createClient();
      await appClient.items.moveObjectApiV1ItemsMovePut({
        requestBody: {
          id: moveObjectWithId,
          move_to: moveToId, // null makes sense here, it means "move to root".
        },
      });

      objects.value
        .filter((item) => item.object.id === moveObjectWithId)
        .forEach((item) => {
          item.object.contained_in_object = nullToUndefined(moveToId);
          item.object.sort_order = nextSortOrderForParent(objects.value, moveToId, moveObjectWithId);
        })
      ;
    }
    async function addObject(item: ObjectCreate) {
      const containedInObjectId = getObjectId(item.contained_in_object);
      const itemToCreate: ObjectCreate = {
        ...item,
        contained_in_object: nullToUndefined(containedInObjectId),
      };
      const appClient = createClient();
      const newId = await appClient.items.createNewObjectApiV1ItemsPut({ requestBody: itemToCreate });
      // noinspection JSUnusedLocalSymbols
      const {
        // ApiObject
        name,
        type,
        logo,
        description,
        url,
        amount_originally,
        amount_current,
        date_acquired,
        date_removed,
        contained_in_object,
        box__id,
        // Barcode
        barcode_type,
        barcode_string,
      } = itemToCreate;
      const unwrappedObject: ApiObject = {
        id: newId,  // we need to use the returned id here
        name,
        type,
        logo: logo as ApiObject["logo"],
        description,
        url,
        amount_originally,
        amount_current,
        date_added: (new Date()).toISOString(),
        date_acquired,
        date_removed,
        sort_order: nextSortOrderForParent(objects.value, containedInObjectId),
        contained_in_object,
        box__id,
        created_at: (new Date()).toISOString(),
        updated_at: (new Date()).toISOString(),
      }
      const unwrappedBarcode: null | ApiBarcode = !barcode_string || barcode_type === null || barcode_type === undefined ? null : {
        code: barcode_string,
        type: barcode_type,
        object__id: newId,  // we can use the returned id here
      }
      const addedObject: ApiObjectWithReferences  = {
        object: unwrappedObject,
        images: [],
        attachments: [],
        barcodes: unwrappedBarcode ? [unwrappedBarcode] : [],
        box: undefined,
      }
      // noinspection TypeScriptValidateJSTypes
      console.log('ADD_OBJECT', {addedObject});
      objects.value.push(addedObject);

      return newId;
    }
    async function duplicateObject(objectId: number, containedInObject?: number | null) {
      const response = await http.post(`${getApiUrl()}/items/duplicate/${objectId}`, {
        contained_in_object: containedInObject ?? null,
      });
      const duplicatedObject = response.data as unknown as ApiObjectWithReferences;
      objects.value.push(duplicatedObject);
      return duplicatedObject;
    }
    async function editObject(
      payload: { id: number, item: ObjectPatch },
    ) {
      const { id, item } = payload;
      const appClient = createClient();
      await appClient.items.editObjectByIdApiV1ItemsIdIdPatch({ id, requestBody: item });

      console.log("EDIT_OBJECT", { id, item });
      objects.value
        .filter((update) => update.object.id === id) // keep only items where the id is the one to edit,
        .forEach((update) => {
          Object.keys(item).forEach((key) => {  // apply every key of the patch to update.object.
            const newValue: any = (item as any)[key];
            if (newValue === undefined) {
              return; // skip unset fields in the patch.
            }
            // @ts-ignore
            update.object[key] = newValue;
          });
        })
      ;
    }
    async function addImage(payload: PhotoToInsert) {
      const { object_id } = payload;
      const appClient = createClient();
      const imageUuid = await appClient.images.createNewImageApiV1ImagesPut({
        requestBody: payload,
      });

      // noinspection TypeScriptValidateJSTypes
      console.log("ADD_IMAGE", { object_id, imageUuid });
      objects.value
        .filter((item) => item.object.id === object_id) // keep only items where the id is the one to move,
        .forEach((item) => {
          item.images.push(imageUuid);
        })
      ;
      return imageUuid;
    }
    async function detachImage(payload: { objectId: number; imageUuid: string }) {
      const { objectId, imageUuid } = payload;
      await http.delete(`${getApiUrl()}/images/detach/${objectId}/${imageUuid}`);
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          item.images = item.images.filter((uuid) => uuid !== imageUuid);
        });
    }
    async function reorderImages(payload: { objectId: number; imageIds: string[] }) {
      const { objectId, imageIds } = payload;
      await http.put(`${getApiUrl()}/images/order/${objectId}`, { image_ids: imageIds });
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          item.images = [...imageIds];
        });
    }
    async function attachExistingImage(payload: { objectId: number; imageUuid: string }) {
      const { objectId, imageUuid } = payload;
      const appClient = createClient();
      await appClient.images.attachExistingImageToObjectApiV1ImagesAttachPut({
        requestBody: { object_id: objectId, photo_id: imageUuid, move: false },
      });
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          if (!item.images.includes(imageUuid)) {
            item.images.push(imageUuid);
          }
        });
    }
    async function listAllImages(): Promise<Array<{ id: string; content_type: string; original_filename: string | null }>> {
      const response = await http.get(`${getApiUrl()}/images/list`);
      return response.data;
    }
    async function addAttachment(payload: { object_id: number; base64: string; original_name?: string | null }) {
      const { object_id } = payload;
      const response = await http.put(`${getApiUrl()}/attachments/`, payload);
      const attachmentUuid = response.data as unknown as string;
      objects.value
        .filter((item) => item.object.id === object_id)
        .forEach((item) => {
          item.attachments.push(attachmentUuid);
        });
      return attachmentUuid;
    }
    async function detachAttachment(payload: { objectId: number; attachmentUuid: string }) {
      const { objectId, attachmentUuid } = payload;
      await http.delete(`${getApiUrl()}/attachments/detach/${objectId}/${attachmentUuid}`);
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          item.attachments = item.attachments.filter((uuid) => uuid !== attachmentUuid);
        });
    }
    async function deleteAttachment(payload: { objectId: number; attachmentUuid: string }) {
      const { objectId, attachmentUuid } = payload;
      await http.delete(`${getApiUrl()}/attachments/${attachmentUuid}`);
      objects.value.forEach((item) => {
        item.attachments = item.attachments.filter((uuid) => uuid !== attachmentUuid);
      });
    }
    async function reorderAttachments(payload: { objectId: number; attachmentIds: string[] }) {
      const { objectId, attachmentIds } = payload;
      await http.put(`${getApiUrl()}/attachments/order/${objectId}`, { attachment_ids: attachmentIds });
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          item.attachments = [...attachmentIds];
        });
    }
    async function attachExistingAttachment(payload: { objectId: number; attachmentUuid: string; move?: boolean; originalObjectId?: number }) {
      const { objectId, attachmentUuid, move = false, originalObjectId } = payload;
      await http.put(`${getApiUrl()}/attachments/attach`, {
        object_id: objectId,
        attachment_id: attachmentUuid,
        move,
        original_object_id: originalObjectId ?? null,
      });
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          if (!item.attachments.includes(attachmentUuid)) {
            item.attachments.push(attachmentUuid);
          }
        });
      if (move && originalObjectId !== undefined) {
        objects.value
          .filter((item) => item.object.id === originalObjectId)
          .forEach((item) => {
            item.attachments = item.attachments.filter((uuid) => uuid !== attachmentUuid);
          });
      }
    }
    async function listAllAttachments(): Promise<Array<{ id: string; content_type: string; original_filename: string | null; file_size: number; uploaded_on: string; object_count: number }>> {
      const response = await http.get(`${getApiUrl()}/attachments/list`);
      return response.data;
    }
    async function getAttachmentInfo(attachmentUuid: string): Promise<{ id: string; content_type: string; original_filename: string | null; file_size: number; uploaded_on: string; object_count: number }> {
      const response = await http.get(`${getApiUrl()}/attachments/${attachmentUuid}/info`);
      return response.data;
    }
    async function listAccounts(): Promise<ProviderAccounts[]> {
      const response = await http.get<ProviderAccounts[]>(`${getApiUrl()}/accounts`);
      return response.data;
    }
    async function loginAccount(payload: { provider: string; username: string; password: string; flavor: string }): Promise<AccountStateResponse> {
      const { provider, ...body } = payload;
      const response = await http.post(`${getApiUrl()}/accounts/${encodeURIComponent(provider)}/login`, body);
      return response.data as unknown as AccountStateResponse;
    }
    async function submitAccountCode(payload: { provider: string; code: string; flavor: string }): Promise<AccountStateResponse> {
      const { provider, ...body } = payload;
      const response = await http.post(`${getApiUrl()}/accounts/${encodeURIComponent(provider)}/code`, body);
      return response.data as unknown as AccountStateResponse;
    }
    async function logoutAccount(payload: { provider: string; flavor: string }): Promise<void> {
      await http.delete(`${getApiUrl()}/accounts/${encodeURIComponent(payload.provider)}`, { params: { flavor: payload.flavor } });
    }
    async function importFromUrl(url: string, containedInObject: number | null = null): Promise<{ object_id: number; name: string; images: string[]; type: typeof OBJECT_TYPE_ITEM }> {
      const response = await http.post(`${getApiUrl()}/import/from-url`, {
        url,
        contained_in_object: containedInObject,
        import_images: true,
      });
      const result = response.data as unknown as { object_id: number; name: string; images: string[]; attachments?: string[] };
      const newObject: ApiObjectWithReferences = {
        object: {
          id: result.object_id,
          name: result.name,
          type: OBJECT_TYPE_ITEM,
          logo: undefined,
          description: null,
          url,
          amount_originally: null,
          amount_current: null,
          date_added: new Date().toISOString(),
          date_acquired: null,
          date_removed: null,
          contained_in_object: containedInObject,
          box__id: null,
          sort_order: nextSortOrderForParent(objects.value, containedInObject),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        images: result.images,
        attachments: result.attachments ?? [],
        barcodes: [],
        box: undefined,
      };
      objects.value.push(newObject);
      return { ...result, type: OBJECT_TYPE_ITEM };
    }
    async function importFetchFromUrl(objectId: number): Promise<{ name: string | null; description: string | null; url: string | null; gallery_image_count: number; gallery_attachment_count: number }> {
      const response = await http.get(`${getApiUrl()}/import/fetch/${objectId}`);
      return response.data;
    }
    async function importUpdateFromUrl(objectId: number): Promise<void> {
      const response = await http.post(`${getApiUrl()}/import/update/${objectId}`);
      const result = response.data as { object_id: number; name: string; images: string[]; attachments: string[] };
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          item.object.name = result.name;
          result.images.forEach((uuid: string) => {
            if (!item.images.includes(uuid)) {
              item.images.push(uuid);
            }
          });
          (result.attachments ?? []).forEach((uuid: string) => {
            if (!item.attachments.includes(uuid)) {
              item.attachments.push(uuid);
            }
          });
        });
    }
    async function addBarcode(barcodeToInsert: BarcodeCreate) {
      const appClient = createClient();
      const imageUuid = await appClient.barcodes.createNewBarcodeApiV1BarcodesPut({
          requestBody: barcodeToInsert,
      });
      // noinspection TypeScriptValidateJSTypes
      console.log("ADD_BARCODE", { barcodeToInsert })
      const id = barcodeToInsert.object__id;
      const payload: ApiBarcode = {
        code: barcodeToInsert.code,
        type: barcodeToInsert.type ?? 0,
        object__id: id,
      };
      objects.value
        .filter((item) => item.object.id === id) // keep only items where the id is the one to move,
        .forEach((item) => {
          item.barcodes.push(payload);
        })
      ;

      return imageUuid;
    }

    async function deleteBarcode({ code, objectId }: { code: string; objectId: number }) {
      await http.delete(`${getApiUrl()}/barcodes/${encodeURIComponent(code)}/${objectId}`);
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          item.barcodes = item.barcodes.filter((b) => b.code !== code);
        });
    }

    async function addCustomField(payload: CustomFieldCreate): Promise<CustomField> {
      const response = await http.post<unknown>(`${getApiUrl()}/custom-fields/`, payload);
      const field = response.data as CustomField;
      objects.value
        .filter((item) => item.object.id === payload.object_id)
        .forEach((item) => {
          if (!item.custom_fields) item.custom_fields = [];
          item.custom_fields.push(field);
        });
      return field;
    }

    async function updateCustomField(payload: { fieldId: CustomFieldId; objectId: number; patch: CustomFieldPatch }): Promise<CustomField> {
      const { fieldId, objectId, patch } = payload;
      const response = await http.patch(`${getApiUrl()}/custom-fields/${fieldId}`, patch);
      const updated = response.data as CustomField;
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          const fields = item.custom_fields;
          if (!fields) return;
          const idx = fields.findIndex((f) => f.id === fieldId);
          if (idx !== -1) fields[idx] = updated;
        });
      return updated;
    }

    async function deleteCustomField(payload: { fieldId: CustomFieldId; objectId: number }): Promise<void> {
      const { fieldId, objectId } = payload;
      await http.delete(`${getApiUrl()}/custom-fields/${fieldId}`);
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          if (!item.custom_fields) return;
          item.custom_fields = item.custom_fields.filter((f) => f.id !== fieldId);
        });
    }

    async function reorderCustomFields(payload: { objectId: number; fieldIds: CustomFieldId[] }): Promise<void> {
      const { objectId, fieldIds } = payload;
      await http.put(`${getApiUrl()}/custom-fields/order/${objectId}`, { field_ids: fieldIds });
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          const fields = item.custom_fields;
          if (!fields) return;
          const byId = new Map(fields.map((f) => [f.id, f]));
          const reordered: CustomField[] = [];
          fieldIds.forEach((id) => { const f = byId.get(id); if (f) reordered.push(f); });
          fields.forEach((f) => { if (!fieldIds.includes(f.id)) reordered.push(f); });
          item.custom_fields = reordered;
        });
    }

    async function addCustomFieldValue(payload: { fieldId: CustomFieldId; objectId: number; value: any }): Promise<CustomFieldValue> {
      const { fieldId, objectId, value } = payload;
      const response = await http.post<unknown>(`${getApiUrl()}/custom-fields/${fieldId}/values`, { value });
      const newVal = response.data as CustomFieldValue;
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          const field = item.custom_fields?.find((f) => f.id === fieldId);
          if (field) field.values.push(newVal);
        });
      return newVal;
    }

    async function updateCustomFieldValue(payload: { fieldId: CustomFieldId; valueId: CustomFieldValueId; objectId: number; value: any }): Promise<CustomFieldValue> {
      const { fieldId, valueId, objectId, value } = payload;
      const response = await http.patch<unknown>(`${getApiUrl()}/custom-fields/${fieldId}/values/${valueId}`, { value });
      const updated = response.data as CustomFieldValue;
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          const field = item.custom_fields?.find((f) => f.id === fieldId);
          if (field) {
            const idx = field.values.findIndex((v) => v.id === valueId);
            if (idx !== -1) field.values[idx] = updated;
          }
        });
      return updated;
    }

    async function deleteCustomFieldValue(payload: { fieldId: CustomFieldId; valueId: CustomFieldValueId; objectId: number }): Promise<void> {
      const { fieldId, valueId, objectId } = payload;
      await http.delete(`${getApiUrl()}/custom-fields/${fieldId}/values/${valueId}`);
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          const field = item.custom_fields?.find((f) => f.id === fieldId);
          if (field) field.values = field.values.filter((v) => v.id !== valueId);
        });
    }

    // --- Schema actions ---

    async function fetchSchemas(): Promise<CustomSchemaOut[]> {
      const response = await http.get<CustomSchemaOut[]>(`${getApiUrl()}/schemas/`);
      schemas.value = response.data;
      return response.data;
    }

    async function createSchema(payload: CustomSchemaCreate): Promise<CustomSchemaOut> {
      const response = await http.post<unknown>(`${getApiUrl()}/schemas/`, payload);
      const schema = response.data as CustomSchemaOut;
      schemas.value.push(schema);
      return schema;
    }

    async function updateSchema(payload: { schemaId: CustomSchemaId; patch: CustomSchemaPatch }): Promise<CustomSchemaOut> {
      const { schemaId, patch } = payload;
      const response = await http.patch<unknown>(`${getApiUrl()}/schemas/${schemaId}`, patch);
      const updated = response.data as CustomSchemaOut;
      const idx = schemas.value.findIndex((s) => s.id === schemaId);
      if (idx !== -1) schemas.value[idx] = updated;
      return updated;
    }

    async function deleteSchema(schemaId: CustomSchemaId): Promise<void> {
      await http.delete(`${getApiUrl()}/schemas/${schemaId}`);
      schemas.value = schemas.value.filter((s) => s.id !== schemaId);
      // Remove from all items that had this schema applied
      objects.value.forEach((item) => {
        if (item.schemas) item.schemas = item.schemas.filter((s) => s.schema_id !== schemaId);
      });
    }

    async function getSchemaJsonSchema(schemaId: CustomSchemaId): Promise<Record<string, any>> {
      const response = await http.get(`${getApiUrl()}/schemas/${schemaId}/openapi`);
      return response.data;
    }

    /** Creates the schemas of an exported OpenAPI document of OpenInv definitions, then reloads the schema list. */
    async function importSchemas(payload: { document: Record<string, unknown>; visibility: SchemaVisibility }): Promise<SchemaImportResult> {
      const response = await http.post<unknown>(`${getApiUrl()}/schemas/import`, payload.document, { params: { visibility: payload.visibility } });
      await fetchSchemas();
      return response.data as SchemaImportResult;
    }

    async function addSchemaEntry(payload: { schemaId: CustomSchemaId; entry: CustomSchemaEntryCreate }): Promise<CustomSchemaOut> {
      const { schemaId, entry } = payload;
      const response = await http.post<unknown>(`${getApiUrl()}/schemas/${schemaId}/entries`, entry);
      const updated = response.data as CustomSchemaOut;
      const idx = schemas.value.findIndex((s) => s.id === schemaId);
      if (idx !== -1) schemas.value[idx] = updated;
      return updated;
    }

    async function updateSchemaEntry(payload: { schemaId: CustomSchemaId; entryId: CustomSchemaEntryId; patch: CustomSchemaEntryPatch }): Promise<CustomSchemaOut> {
      const { schemaId, entryId, patch } = payload;
      const response = await http.patch<unknown>(`${getApiUrl()}/schemas/${schemaId}/entries/${entryId}`, patch);
      const updated = response.data as CustomSchemaOut;
      const idx = schemas.value.findIndex((s) => s.id === schemaId);
      if (idx !== -1) schemas.value[idx] = updated;
      return updated;
    }

    async function deleteSchemaEntry(payload: { schemaId: CustomSchemaId; entryId: CustomSchemaEntryId }): Promise<void> {
      const { schemaId, entryId } = payload;
      await http.delete(`${getApiUrl()}/schemas/${schemaId}/entries/${entryId}`);
      const schema = schemas.value.find((s) => s.id === schemaId);
      if (schema) schema.entries = schema.entries.filter((e) => e.id !== entryId);
    }

    async function reorderSchemaEntries(payload: { schemaId: CustomSchemaId; entryIds: CustomSchemaEntryId[] }): Promise<void> {
      const { schemaId, entryIds } = payload;
      await http.put(`${getApiUrl()}/schemas/${schemaId}/entries/order`, { entry_ids: entryIds });
      const schema = schemas.value.find((s) => s.id === schemaId);
      if (schema) {
        const byId = new Map(schema.entries.map((e) => [e.id, e]));
        const reordered = entryIds.map((id) => byId.get(id)).filter(Boolean) as typeof schema.entries;
        schema.entries.forEach((e) => { if (!entryIds.includes(e.id)) reordered.push(e); });
        schema.entries = reordered;
      }
    }

    async function fetchObjectSchemas(objectId: number): Promise<ObjectSchemaOut[]> {
      const response = await http.get<ObjectSchemaOut[]>(`${getApiUrl()}/schemas/object/${objectId}`);
      const data = response.data;
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => { item.schemas = data; });
      return data;
    }

    async function applySchemaToObject(payload: { objectId: number; schemaId: CustomSchemaId }): Promise<ObjectSchemaOut> {
      const { objectId, schemaId } = payload;
      const response = await http.post<unknown>(`${getApiUrl()}/schemas/object/${objectId}/apply/${schemaId}`, {});
      const applied = response.data as ObjectSchemaOut;
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          if (!item.schemas) item.schemas = [];
          item.schemas.push(applied);
        });
      return applied;
    }

    async function removeSchemaFromObject(payload: { objectId: number; schemaId: CustomSchemaId }): Promise<void> {
      const { objectId, schemaId } = payload;
      await http.delete(`${getApiUrl()}/schemas/object/${objectId}/remove/${schemaId}`);
      objects.value
        .filter((item) => item.object.id === objectId)
        .forEach((item) => {
          if (item.schemas) item.schemas = item.schemas.filter((s) => s.schema_id !== schemaId);
        });
    }

    async function reorderObjectSchemas(payload: { objectId: number; schemaIds: CustomSchemaId[] }): Promise<void> {
      const { objectId, schemaIds } = payload;
      await http.put(`${getApiUrl()}/schemas/object/${objectId}/order`, { schema_ids: schemaIds });
    }

    async function setSchemaFieldValue(payload: { objectId: number; entryId: CustomSchemaEntryId; value: any }): Promise<CustomSchemaValueOut> {
      const { objectId, entryId, value } = payload;
      const response = await http.put<unknown>(`${getApiUrl()}/schemas/object/${objectId}/values/${entryId}`, { value });
      const val = response.data as CustomSchemaValueOut;
      // Refresh object schemas to get updated values
      await fetchObjectSchemas(objectId);
      return val;
    }

    async function addSchemaFieldValue(payload: { objectId: number; entryId: CustomSchemaEntryId; value: any }): Promise<CustomSchemaValueOut> {
      const { objectId, entryId, value } = payload;
      const response = await http.post<unknown>(`${getApiUrl()}/schemas/object/${objectId}/values/${entryId}`, { value });
      const val = response.data as CustomSchemaValueOut;
      await fetchObjectSchemas(objectId);
      return val;
    }

    async function deleteSchemaFieldValue(payload: { objectId: number; entryId: CustomSchemaEntryId; valueId: CustomSchemaValueId }): Promise<void> {
      const { objectId, entryId, valueId } = payload;
      await http.delete(`${getApiUrl()}/schemas/object/${objectId}/values/${entryId}/${valueId}`);
      await fetchObjectSchemas(objectId);
    }

    async function reorderSchemaFieldValues(payload: { objectId: number; entryId: CustomSchemaEntryId; valueIds: CustomSchemaValueId[] }): Promise<void> {
      const { objectId, entryId, valueIds } = payload;
      await http.put(`${getApiUrl()}/schemas/object/${objectId}/values/${entryId}/order`, { entry_ids: valueIds });
      await fetchObjectSchemas(objectId);
    }

    async function reorderObjects(payload: { containerId: number | null; objectIds: number[] }) {
      const { containerId, objectIds } = payload;
      await http.put(`${getApiUrl()}/items/order`, {
        container_id: containerId,
        object_ids: objectIds,
      });

      const currentSiblings = objects.value
        .filter((item) => (item.object.contained_in_object ?? null) === containerId)
        .sort(compareObjectOrder);
      const currentById = new Map(currentSiblings.map((item) => [item.object.id, item]));
      const submitted: ApiObjectWithReferences[] = [];
      const seenIds = new Set<number>();
      objectIds.forEach((objectId) => {
        if (seenIds.has(objectId)) return;
        seenIds.add(objectId);
        const item = currentById.get(objectId);
        if (item !== undefined) {
          submitted.push(item);
        }
      });
      const submittedIds = new Set(submitted.map((item) => item.object.id));
      const omitted = currentSiblings.filter((item) => !submittedIds.has(item.object.id));
      [...submitted, ...omitted].forEach((item, index) => {
        item.object.sort_order = index + 1;
      });
    }

    return {
      // state
      objects,
      schemas,
      fetchedIcons,
      iconNames,
      accessToken,
      imagesToken,
      // computed
      items,
      containers,
      // objectsByID,  // not exposed.
      objectsAtRoot,
      allIconNames,
      allIconSvgMetas,
      // getter
      byId,
      iconSvgMeta,
      // setter
      setAccessToken,
      setImagesToken,
      // token refresh
      scheduleTokenRefresh,
      clearTokenRefresh,
      // actions
      fetchObjects,
      fetchIcon,
      fetchIcons,
      isIconPending,
      fetchAllIcons,
      fetchAllIconNames,
      deleteItem,
      moveObject,
      addObject,
      duplicateObject,
      editObject,
      addImage,
      detachImage,
      reorderImages,
      attachExistingImage,
      listAllImages,
      addAttachment,
      detachAttachment,
      deleteAttachment,
      reorderAttachments,
      attachExistingAttachment,
      listAllAttachments,
      getAttachmentInfo,
      listAccounts,
      loginAccount,
      submitAccountCode,
      logoutAccount,
      importFromUrl,
      importFetchFromUrl,
      importUpdateFromUrl,
      addBarcode,
      deleteBarcode,
      reorderObjects,
      addCustomField,
      updateCustomField,
      deleteCustomField,
      reorderCustomFields,
      addCustomFieldValue,
      updateCustomFieldValue,
      deleteCustomFieldValue,
      fetchSchemas,
      createSchema,
      updateSchema,
      deleteSchema,
      getSchemaJsonSchema,
      importSchemas,
      addSchemaEntry,
      updateSchemaEntry,
      deleteSchemaEntry,
      reorderSchemaEntries,
      fetchObjectSchemas,
      applySchemaToObject,
      removeSchemaFromObject,
      reorderObjectSchemas,
      setSchemaFieldValue,
      addSchemaFieldValue,
      deleteSchemaFieldValue,
      reorderSchemaFieldValues,
    };
  },
  {
    // pinia-plugin-persistedstate
    // https://www.npmjs.com/package/pinia-plugin-persistedstate
    // (https://github.com/prazdevs/pinia-plugin-persistedstate)
    // https://codeberg.org/praz/pinia-plugin-persistedstate/
    // https://prazdevs.github.io/pinia-plugin-persistedstate/guide/limitations.html#storage-must-be-synchronous
    persist: false, // we do NOT persist the api store, as it is probably better fetched freshly from the API on start.

    // pinia-shared-state
    // https://www.npmjs.com/package/pinia-shared-state
    // (https://wobsoriano.github.io/pinia-shared-state/)
    // https://github.com/wobsoriano/pinia-shared-state
    share: {
      // An array of fields that the plugin will ignore.
      // omit: ['foo'],
      // Override global config for this store.
      enable: true,
      // If set to true this tab tries to immediately recover the shared state from another tab. Defaults to true.
      initialize: true,  // should be safe, as it's gonna be kinda up-to-date data? And the mount hooks should fetch fresh data anyway.
    },
  },
);
